import React from 'react';
import {
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  PencilIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function BackToWorkFormHelp() {
  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-teal-600 p-3 rounded-full">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Back-to-Work Form Guide</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete guide to submitting return-to-work forms and late arrival explanations,
            ensuring smooth reintegration and maintaining accurate attendance records.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ShieldCheckIcon className="h-6 w-6 text-teal-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Back-to-Work Form serves dual purposes: documenting your return to work after
            an absence and explaining late arrivals. This ensures proper health and safety compliance,
            maintains accurate attendance records, and facilitates smooth reintegration into the workplace.
          </p>
        </div>

        {/* Form Types */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-teal-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Form Types</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <CalendarDaysIcon className="h-6 w-6 text-teal-600 mr-3" />
                <h3 className="text-xl font-semibold text-teal-800">Return-to-Work Form</h3>
              </div>
              <p className="text-teal-700 mb-4">For employees returning after planned or unplanned absences</p>
              <ul className="space-y-2 text-teal-600">
                <li>• Medical absences and sick leave</li>
                <li>• Annual leave and personal time off</li>
                <li>• Maternity/paternity leave returns</li>
                <li>• Other extended absences</li>
              </ul>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <div className="flex items-center mb-3">
                <ClockIcon className="h-6 w-6 text-orange-600 mr-3" />
                <h3 className="text-xl font-semibold text-orange-800">Late Arrival Form</h3>
              </div>
              <p className="text-orange-700 mb-4">For explaining delays in starting work</p>
              <ul className="space-y-2 text-orange-600">
                <li>• Traffic or transportation issues</li>
                <li>• Medical appointments</li>
                <li>• Family emergencies</li>
                <li>• Unforeseen circumstances</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Return-to-Work Process */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CalendarDaysIcon className="h-6 w-6 text-teal-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Return-to-Work Process</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-teal-600 font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Submit Form</h3>
              <p className="text-gray-600 text-sm">Complete the return-to-work form with your details</p>
            </div>

            <div className="text-center">
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-teal-600 font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Manager Review</h3>
              <p className="text-gray-600 text-sm">HR reviews your submission and readiness</p>
            </div>

            <div className="text-center">
              <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-teal-600 font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Return to Work</h3>
              <p className="text-gray-600 text-sm">Resume duties with any required adjustments</p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Form Fields */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <PencilIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Form Fields</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Staff Information:</strong> Pre-filled with your name and manager details</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Return Date:</strong> When you plan to return to work</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Start Date:</strong> Actual date you begin working again</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Reason for Absence:</strong> Brief explanation of your absence</span>
              </li>
            </ul>
          </div>

          {/* Health & Safety */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Health & Safety</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Fitness for Work:</strong> Confirm you are medically fit to return</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Adjustments Needed:</strong> Specify any workplace modifications required</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Medical Clearance:</strong> Provide doctor's note if required by policy</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Safety Considerations:</strong> Highlight any ongoing health concerns</span>
              </li>
            </ul>
          </div>

          {/* Late Arrival Process */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Late Arrival Process</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Immediate Notification:</strong> Submit explanation as soon as possible</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Clear Explanation:</strong> Provide specific, honest reasons for delay</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Character Limit:</strong> Keep explanations concise (under 500 characters)</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Documentation:</strong> Information is recorded for attendance tracking</span>
              </li>
            </ul>
          </div>

          {/* Digital Signature */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <PencilIcon className="h-6 w-6 text-teal-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Digital Signature</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Legal Validity:</strong> Digital signatures have legal standing</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Secure Capture:</strong> Signatures are securely stored and timestamped</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Easy to Use:</strong> Draw signature with finger or stylus on touch devices</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Audit Trail:</strong> All signatures are logged with date and time</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Important Guidelines */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <ExclamationTriangleIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Important Guidelines</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Health & Safety Compliance</h3>
              <ul className="space-y-2 text-teal-100">
                <li>• Always be honest about your fitness for work</li>
                <li>• Report any ongoing health concerns</li>
                <li>• Request reasonable adjustments if needed</li>
                <li>• Follow medical advice regarding return dates</li>
                <li>• Keep medical certificates confidential</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Attendance Policy</h3>
              <ul className="space-y-2 text-teal-100">
                <li>• Submit forms promptly upon return</li>
                <li>• Provide accurate and complete information</li>
                <li>• Keep explanations professional and factual</li>
                <li>• Understand company attendance policies</li>
                <li>• Contact HR if you need assistance</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Tips for Successful Submissions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 Form Completion</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Complete forms in a quiet, distraction-free environment</li>
                <li>• Double-check all information before submitting</li>
                <li>• Save drafts if you need to step away</li>
                <li>• Use clear, professional language</li>
                <li>• Include all required medical information</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🏥 Health Considerations</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Consult your doctor before confirming fitness for work</li>
                <li>• Be specific about any adjustments you need</li>
                <li>• Consider gradual return-to-work if appropriate</li>
                <li>• Keep records of medical communications</li>
                <li>• Understand your rights under health regulations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use landscape mode for better signature capture</li>
                <li>• Ensure stable internet connection for submission</li>
                <li>• Test signature pad before final submission</li>
                <li>• Use stylus for more precise signatures</li>
                <li>• Review form on larger screen if possible</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⏰ Late Arrival Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Submit explanations immediately when you arrive</li>
                <li>• Be honest and provide verifiable reasons</li>
                <li>• Keep explanations brief but informative</li>
                <li>• Avoid excuses that could be seen as unreasonable</li>
                <li>• Learn from patterns to improve punctuality</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Privacy & Security</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Medical information is kept confidential</li>
                <li>• Only authorized personnel can access forms</li>
                <li>• Data is encrypted and securely stored</li>
                <li>• Forms comply with data protection laws</li>
                <li>• You can request access to your own records</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📞 Support Resources</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Contact HR for assistance with forms</li>
                <li>• Seek medical advice for health-related questions</li>
                <li>• Use employee assistance programs if needed</li>
                <li>• Consult union representatives for guidance</li>
                <li>• Access company policies online</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}